import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Users,
  Search,
  Shield,
  UserCheck,
  UserX,
  Info,
} from "../components/icons";
import { apiClient } from "../lib/api";
import type { UserData, UserRole } from "../types";
import { useAuthStore } from "../store/authStore";
import LoadingSpinner from "../components/LoadingSpinner";

export default function UsersManager() {
  const { t } = useTranslation("admin");
  const currentUser = useAuthStore((state) => state.user);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [newRole, setNewRole] = useState<UserRole>("guest");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("🔍 Fetching users from /admin/users...");
      const data = await apiClient.get<UserData[]>("/admin/users");
      console.log("✅ Users fetched successfully:", data);
      setUsers(data);
    } catch (err: any) {
      console.error("❌ Error fetching users:", err);
      console.error("Response:", err.response);
      setError(err.response?.data?.detail || t("errorFetchingData"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;

    try {
      setError(null);
      await apiClient.put(`/admin/users/${selectedUser._id}`, {
        role: newRole,
      });
      setSuccess(t("roleUpdated"));
      await fetchUsers();
      setShowRoleModal(false);
      setSelectedUser(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || t("errorSavingData"));
    }
  };

  const handleToggleActive = async (user: UserData) => {
    // Prevent users from deactivating themselves
    if (user.firebase_uid === currentUser?.uid) {
      setError(t("cannotDeactivateSelf"));
      setTimeout(() => setError(null), 3000);
      return;
    }

    const newStatus = !user.is_active;
    const confirmMessage = newStatus
      ? t("confirmActivate")
      : t("confirmDeactivate");

    if (!window.confirm(confirmMessage)) return;

    try {
      setError(null);
      await apiClient.put(`/admin/users/${user._id}`, { is_active: newStatus });
      setSuccess(newStatus ? t("userActivated") : t("userDeactivated"));
      await fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || t("errorSavingData"));
    }
  };

  const handleOpenRoleModal = (user: UserData) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowRoleModal(true);
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case "admin":
        return "bg-primary-100 text-primary-800";
      case "analyst":
        return "bg-green-100 text-green-800";
      case "guest":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firebase_uid.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        role="status"
        aria-live="polite"
      >
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Users className="h-8 w-8 text-primary-600" aria-hidden="true" />
          <h1
            className="text-3xl font-bold text-gray-900"
            data-testid="users-manager-heading"
          >
            {t("userManagement")}
          </h1>
        </div>
      </div>

      <div aria-live="polite" aria-atomic="true">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="inline-flex items-center justify-center h-6 w-6 rounded text-red-600 hover:text-red-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        )}

        {success && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-center justify-between">
            <span>{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="inline-flex items-center justify-center h-6 w-6 rounded text-green-600 hover:text-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
              aria-label="Dismiss success"
            >
              ×
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            data-testid="users-search-input"
            placeholder={t("search")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
      </div>

      <div className="card">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchTerm ? t("noData") : t("noUsers")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-gray-200"
              data-testid="users-table"
            >
              <thead className="table-header">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {t("email")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {t("displayName")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {t("role")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {t("userStatus")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {t("accountCreated")}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="data-rows">
                {filteredUsers.map((user) => (
                  <tr
                    key={user._id}
                    data-testid={`user-row-${user._id}`}
                    className={`hover:bg-gray-50 transition-colors ${
                      !user.is_active ? "opacity-60" : ""
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="flex items-center gap-3">
                        {user.photo_url && (
                          <img
                            src={user.photo_url}
                            alt=""
                            className="h-8 w-8 rounded-full flex-shrink-0"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.email}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            {user.firebase_uid.substring(0, 12)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.display_name || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleBadgeColor(
                          user.role,
                        )}`}
                      >
                        {t(`roles.${user.role}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          user.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {user.is_active ? t("active") : t("inactive")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenRoleModal(user)}
                          data-testid={`btn-update-role-${user._id}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-primary-600 hover:text-primary-800 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors"
                          title={t("updateRole")}
                          aria-label={`${t("updateRole")}: ${
                            user.display_name || user.email
                          }`}
                        >
                          <Shield className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          data-testid={`toggle-active-${user._id}`}
                          className={`inline-flex items-center justify-center h-8 w-8 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            user.is_active
                              ? "text-orange-600 hover:text-orange-800 hover:bg-orange-50 focus-visible:ring-orange-500"
                              : "text-green-600 hover:text-green-800 hover:bg-green-50 focus-visible:ring-green-500"
                          }`}
                          title={
                            user.is_active
                              ? t("deactivateUser")
                              : t("activateUser")
                          }
                          disabled={user.firebase_uid === currentUser?.uid}
                        >
                          {user.is_active ? (
                            <UserX className="h-5 w-5" aria-hidden="true" />
                          ) : (
                            <UserCheck className="h-5 w-5" aria-hidden="true" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Role Update Modal */}
      {showRoleModal && selectedUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          data-testid="role-update-modal"
        >
          <div className="rounded-xl border border-gray-200 bg-white shadow-xl max-w-2xl w-full">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t("updateRole")}
              </h2>
              <p className="text-sm text-gray-500 mt-1">{selectedUser.email}</p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t("role")} *
                </label>
                <div className="space-y-3">
                  {(["admin", "analyst", "guest"] as UserRole[]).map((role) => (
                    <label
                      key={role}
                      className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        newRole === role
                          ? "border-primary-500 bg-primary-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role}
                        data-testid={`role-radio-${role}`}
                        checked={newRole === role}
                        onChange={(e) => setNewRole(e.target.value as UserRole)}
                        className="mt-1 h-4 w-4 text-primary-600"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleBadgeColor(
                              role,
                            )}`}
                          >
                            {t(`roles.${role}`)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-2 flex items-start">
                          <Info
                            className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0"
                            aria-hidden="true"
                          />
                          {t(`roleDescriptions.${role}`)}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  {t("details")}
                </h3>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">{t("accountCreated")}:</dt>
                    <dd className="text-gray-900">
                      {formatDate(selectedUser.created_at)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">{t("lastUpdated")}:</dt>
                    <dd className="text-gray-900">
                      {formatDate(selectedUser.updated_at)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">{t("userStatus")}:</dt>
                    <dd className="text-gray-900">
                      {selectedUser.is_active ? t("active") : t("inactive")}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                }}
                className="btn btn-secondary"
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                data-testid="btn-save-role"
                onClick={handleUpdateRole}
                className="btn btn-primary"
                disabled={newRole === selectedUser.role}
              >
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
